import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import OnboardingLayout from "./OnboardingLayout";
import { Button } from "../../components/ui/button";
import SampleReportPreview from "../../components/SampleReportPreview";
import { CheckCircle2, ArrowRight, Rocket, Share2, Users } from "lucide-react";

const STEPS = [
  {
    icon: Rocket,
    title: "Configure a Growth Campaign",
    body: "Name it, pick a market — the AI writes the questions, landing page and outreach.",
  },
  {
    icon: Share2,
    title: "Share your branded link",
    body: "Copy the link, download the QR, or use pre-filled LinkedIn / WhatsApp / email templates.",
  },
  {
    icon: Users,
    title: "Receive qualified leads",
    body: "Every completion arrives with a scored lead and an Executive Growth Report ready to send.",
  },
];

export default function Step3Welcome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const first = (user?.name || "").split(" ")[0] || "there";

  return (
    <OnboardingLayout currentStep="practice" hideRail maxWidth="max-w-2xl">
      <div className="flex flex-col items-start">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs uppercase tracking-widest font-sans-ui"
          data-testid="welcome-badge"
        >
          <CheckCircle2 className="w-4 h-4" /> Practice saved
        </div>
        <h1
          className="mt-6 font-display text-4xl sm:text-5xl leading-tight text-foreground"
          data-testid="welcome-headline"
        >
          You&apos;re set, {first}.
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-lg">
          One task left: launch your first Growth Campaign. It takes about
          three minutes.
        </p>
      </div>

      <ol
        className="mt-8 space-y-3 border-l border-border/60 pl-6"
        data-testid="welcome-three-steps"
      >
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="relative flex items-start gap-4 py-2">
              <div className="absolute -left-[34px] top-2 w-6 h-6 rounded-full bg-background border border-accent/40 text-accent flex items-center justify-center">
                <Icon className="w-3 h-3" />
              </div>
              <div>
                <div className="font-sans-ui text-xs uppercase tracking-widest text-muted-foreground">
                  Step {i + 1}
                </div>
                <div className="mt-0.5 font-sans-ui font-semibold text-base leading-snug text-foreground">
                  {s.title}
                </div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  {s.body}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <SampleReportPreview />

      <div className="mt-10 flex items-center justify-between gap-4">
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground transition"
          onClick={() => nav("/dashboard")}
          data-testid="welcome-skip"
        >
          Skip for now
        </button>
        <Button
          size="lg"
          className="px-8 h-12 text-base"
          onClick={() => nav("/onboarding/first-campaign")}
          data-testid="welcome-launch-cta"
        >
          Launch first campaign
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </OnboardingLayout>
  );
}
